/**
 * @file Event List Factory
 *
 * @author jonas.huber@notch-interactive.com, christian.sany@notch-interactive.com
 */

import Vue from 'vue/dist/vue.min.js';

import VueI18n from 'vue-i18n';
import eventDetail from '../vue-components/event-detail/event-detail.vue';
import { scroll } from '../helpers/smoothscroll';

let defaults = {
    hook: '[data-event-id]',
    idAttr: 'data-event-id',
    closeHook: '[data-hide-details]',
    openHook: '[data-show-details]',
    detailHook: '[data-detail]'
};

// Register i18n plugin
Vue.use(VueI18n);

// Set language
Vue.config.lang = window.DS.lang;

// Add translations
Vue.locale(window.DS.lang, window.DS.translations);

// Event list factory
export default (el, options) => {

    const instance = {};
    const settings = Object.assign({}, defaults, options);
    const tiles = [];

    // Private functions

    /**
     * Delegate handler
     *
     * @param {event} e - passed event
     */
    const delegate = (e) => {

        // Closer was clicked
        if (e.target.closest(settings.closeHook) !== null) {
            e.preventDefault();

            instance.close();
        }

        // Opener was clicked
        if (e.target.closest(settings.openHook) !== null) {
            e.preventDefault();

            instance.open(e.target.closest(settings.hook).getAttribute(settings.idAttr));
        }
    };

    /**
     * Bind delegate handler
     */
    const bindHandlers = () => {
        el.addEventListener('click', delegate);
    };


    /**
     * Unbind delegate handler
     */
    const unbindHandlers = () => {
        el.removeEventListener('click', delegate);
    };

    // Public functions

    /**
     * Closes the open event detail
     *
     * @return {promise} resolves promise
     */
    instance.close = () => {
        return new Promise((resolve) => {
            let openTiles = tiles.filter((tile) => tile.open);

            // Check if open Tile is found
            if (openTiles.length) {

                openTiles[0].el.querySelector(settings.detailHook).addEventListener('transitionend', function transitionListener() {
                    openTiles[0].open = false;
                    openTiles[0].el.querySelector(settings.detailHook).removeEventListener('transitionend', transitionListener);

                    // Resolve promise when finished closing
                    resolve();
                });

                openTiles[0].el.classList.remove('is-open');
            } else {

                // Nothing needs to close -> resolve promise
                resolve();
            }
        });
    };

    /**
     * Opens a event detail view
     * Closes open detail view if necessary
     *
     * @param {any} id of the event
     */
    instance.open = (id) => {

        // Call close function in case something needs to close
        instance.close()
            .then(() => {
                let tilesToOpen = tiles.filter((tile) => tile.id === id);

                tilesToOpen[0].el.classList.add('is-open');
                tilesToOpen[0].open = true;


                // Check if some scrolling is appropriate for a better ux
                const offset = tilesToOpen[0].el.getBoundingClientRect();

                // Check if scrolling needs to be done (when tile is under the navigation, or when tile is in second half of the viewport)
                if (window.innerHeight / 2 < offset.top || offset.top < 90) {

                    // Give the detail container some time to open, befor animating there
                    setTimeout(() => {
                        scroll(tilesToOpen[0].el, {
                            offset: (top) => top > 180 ? -90 : -165,
                            duration: 500
                        });
                    }, 70);
                }
            });
    };

    /**
     * Destroys this factory
     */
    instance.destroy = () => {
        unbindHandlers();

        // Destroy all vue instances
        tiles.forEach((tile) => {
            tile.vm.$destroy();
        });
    };

    // Init script
    const tileNodes = el.querySelectorAll(settings.hook);

    // Create the vue-instances and save them in an array with the corresponding node
    tileNodes.forEach((el) => {
        let vm = new Vue({
            el: el.querySelector(settings.detailHook),
            components: {
                eventDetail
            },
            data: {
                compact: false
            }
        });

        tiles.push({
            id: el.getAttribute(settings.idAttr),
            open: false,
            el,
            vm
        });
    });

    bindHandlers();

    return instance;
};
